import "./Banner.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import { buildMediaUrl, getDirectPlayPath } from "../services/streamApi";

function truncateText(value, limit = 48) {
  const text = String(value || "");
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
}

function Banner({ featuredItem, featuredItems = [], onMoreInfo, eyebrow, descriptionOverride, isLoading }) {
  const items = useMemo(() => {
    if (featuredItems?.length) return featuredItems;
    return featuredItem ? [featuredItem] : [];
  }, [featuredItem, featuredItems]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const transitionTimeoutRef = useRef(null);

  useEffect(() => {
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }

    setCurrentIndex(0);
    setFade(true);
  }, [items]);

  useEffect(() => {
    if (items.length <= 1) return undefined;

    const interval = window.setInterval(() => {
      setFade(false);
      transitionTimeoutRef.current = window.setTimeout(() => {
        setCurrentIndex((previous) => (previous + 1) % items.length);
        setFade(true);
        transitionTimeoutRef.current = null;
      }, 480);
    }, 8200);

    return () => {
      window.clearInterval(interval);
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, [items]);

  if (isLoading) {
    return <header className="banner banner--loading" />;
  }

  const activeItem = items[currentIndex] || featuredItem;

  if (!activeItem) {
    return (
      <header className="banner banner--loading">
        <div className="banner__content">
          <p className="banner__eyebrow">Catalogue</p>
          <h1 className="banner__title">Votre plateforme streaming est prete.</h1>
          <p className="banner__description">
            Ajoutez vos contenus et profitez d&apos;une interface premium inspiree de Netflix.
          </p>
        </div>
      </header>
    );
  }

  const typeLabel = activeItem.type === "film" ? "Film" : activeItem.type === "serie" ? "Serie" : "Documentaire";
  const metadata = [activeItem.year, activeItem.categoryLabel, activeItem.durationLabel, typeLabel].filter(Boolean);
  const progressValue = activeItem.resume?.percent || 0;
  const eyebrowLabel = activeItem.heroContextLabel || eyebrow || "Nouveau sur Streamflix";
  const primaryCta = progressValue > 3 && progressValue < 95 ? "Reprendre" : "Lecture";
  const displayTitle = truncateText(activeItem.title, 30);

  return (
    <header
      className={`banner ${fade ? "fade-in" : "fade-out"}`}
      style={{
        "--banner-bg": `url("${buildMediaUrl(activeItem.backdropPath || activeItem.posterPath)}")`,
      }}
    >
      <div key={`banner-stage-${activeItem.id}-${currentIndex}`} className="banner__content">
        <p className="banner__eyebrow">{eyebrowLabel}</p>
        <h1 className="banner__title" title={activeItem.title}>
          {displayTitle}
        </h1>
        <div className="banner__meta">
          {metadata.map((value, index) => (
            <span key={`${activeItem.id}-${index}-${value}`} className="banner__metaItem">
              {value}
            </span>
          ))}
          <span className="banner__metaItem banner__metaItem--match">
            {activeItem.matchScore || Math.min(99, 72 + activeItem.likes * 3 + activeItem.views)} % Match
          </span>
          {activeItem.isNewRelease ? <span className="banner__metaItem banner__metaItem--new">Nouveau</span> : null}
        </div>
        <p className="banner__description">
          {descriptionOverride ||
            (activeItem.description?.length > 150
              ? `${activeItem.description.slice(0, 150)}...`
              : activeItem.description || "Selection premium a regarder maintenant.")}
        </p>
        <div className="banner__buttons">
          <Link to={getDirectPlayPath(activeItem)} className="banner__button banner__button--play">
            <PlayArrowIcon /> {primaryCta}
          </Link>
          <button className="banner__button" onClick={() => onMoreInfo?.(activeItem)}>
            <HelpOutlineIcon /> Plus d&apos;infos
          </button>
        </div>

        {progressValue > 3 && progressValue < 95 ? (
          <div className="banner__resume">
            <span>Reprise a {progressValue}%</span>
            <div className="banner__resumeBar">
              <span style={{ width: `${progressValue}%` }} />
            </div>
          </div>
        ) : null}

        {items.length > 1 ? (
          <div className="banner__indicators">
            {items.map((item, index) => (
              <button
                key={item.id}
                className={`banner__indicator ${index === currentIndex ? "banner__indicator--active" : ""}`}
                onClick={() => {
                  setFade(false);
                  if (transitionTimeoutRef.current) {
                    window.clearTimeout(transitionTimeoutRef.current);
                  }
                  transitionTimeoutRef.current = window.setTimeout(() => {
                    setCurrentIndex(index);
                    setFade(true);
                    transitionTimeoutRef.current = null;
                  }, 180);
                }}
                aria-label={`Aller a ${item.title}`}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="banner__badge">
        <VolumeUpOutlinedIcon />
        <span>Lecture immersive</span>
      </div>
    </header>
  );
}

export default Banner;
